import { useCallback, useMemo, useState } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type Modifier,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { DirectoryItem } from './theme';

/**
 * How far the pointer travels before a press becomes a drag.
 *
 * The Directory's rows are the panel's select targets first and its reorder targets second, so this
 * number is the whole of the boundary between the two: under it a press is a click and opens the
 * item, over it the row lifts and nothing is selected. 8px is the codebase's existing figure
 * (`OutlinePanel/hooks/useDndSensors`) and it is a good one — far enough that a hand resting on a
 * trackpad cannot cross it, close enough that a deliberate drag never feels like it has to be
 * argued for.
 */
const DRAG_THRESHOLD = 8;

/**
 * Rows travel on one axis only.
 *
 * `@dnd-kit/modifiers` is not a dependency here and this is the whole of what would be imported
 * from it. A row that can drift sideways while the list it belongs to cannot is lying about where
 * it can go — the list is 252px wide with one column, and the only question a drag asks is which
 * gap. The image GRID gets no modifier, because there the sideways answer is real.
 */
const lockToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

export interface DirectoryReorderOptions {
  /** The list on screen, in its current order. */
  items: DirectoryItem[];
  /** The new order, whole. The panel is controlled, so it reports and the caller applies. */
  onReorder?: (ids: string[]) => void;
  /** Images sort in two dimensions; every other kind is one column. */
  grid?: boolean;
}

/**
 * Everything the panel needs to make its list sortable, and nothing about how it looks.
 *
 * A hook rather than a component because the sensors, the id list and the active item are one piece
 * of state that both the list and the drag overlay read — and because `useSortable` has to be
 * called once per row, which a parent cannot do in a loop.
 */
export function useDirectoryReorder({ items, onReorder, grid = false }: DirectoryReorderOptions) {
  /** The item under the pointer, or null. It is what the overlay draws. */
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_THRESHOLD } }),
    // Space picks a row up, the arrows move it, Space drops it and Escape puts it back — and
    // `DndContext` announces every one of those in a live region without being asked. It is the
    // only path through this feature that does not require a pointer, and it costs one sensor.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = useMemo(() => items.map((item) => item.id), [items]);
  const activeItem = activeId ? (items.find((item) => item.id === activeId) ?? null) : null;

  const onDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      // No `over` is a drop outside every gap; the same id is a drop back where it started. Neither
      // is a reorder, and reporting one would push a caller into a state write and a re-render for
      // an order that did not change.
      if (!over || active.id === over.id) return;
      const from = ids.indexOf(String(active.id));
      const to = ids.indexOf(String(over.id));
      if (from === -1 || to === -1) return;
      onReorder?.(arrayMove(ids, from, to));
    },
    [ids, onReorder],
  );

  /** Escape, or a drop the sensor abandoned. The list snaps back on its own; the overlay must go. */
  const onDragCancel = useCallback(() => setActiveId(null), []);

  /**
   * What the live region says, in the deck's own words.
   *
   * dnd-kit ships defaults, and they say "Sortable item 3 was moved into position 5" — true, and
   * useless in a list where the whole point of an item is which artifact it is. Every announcement
   * here names the thing and counts from 1, because "position 5 of 8" is what a person means and
   * an array index is not.
   */
  const announcements = useMemo(() => {
    const title = (id: string | number) =>
      items.find((item) => item.id === String(id))?.title ?? String(id);
    const place = (id: string | number) => `${ids.indexOf(String(id)) + 1} of ${ids.length}`;

    return {
      onDragStart: ({ active }: { active: { id: string | number } }) =>
        `Picked up ${title(active.id)}. It is at position ${place(active.id)}.`,
      onDragOver: ({ active, over }: { active: { id: string | number }; over: { id: string | number } | null }) =>
        over
          ? `${title(active.id)} would move to position ${place(over.id)}.`
          : `${title(active.id)} is not over a place it can go.`,
      onDragEnd: ({ active, over }: { active: { id: string | number }; over: { id: string | number } | null }) =>
        over
          ? `${title(active.id)} dropped at position ${place(over.id)}.`
          : `${title(active.id)} was dropped and stayed where it was.`,
      onDragCancel: ({ active }: { active: { id: string | number } }) =>
        `Reordering cancelled. ${title(active.id)} is back at position ${place(active.id)}.`,
    };
  }, [items, ids]);

  return {
    sensors,
    ids,
    activeId,
    activeItem,
    announcements,
    modifiers: grid ? undefined : [lockToVerticalAxis],
    onDragStart,
    onDragEnd,
    onDragCancel,
  };
}
